import {
	BLOOD_KEY_TYPE,
	CALENDAR_KEY_TYPE,
	CHINESE_ZODIAC_SYMBOLS_KEY_TYPE,
	COMPANY_STATUS_KEY_TYPE,
	CORPORATION_OPERATION_KEY_TYPE,
	EDUCATION_KEY_TYPE,
	FC_CERTIFICATES_KEY_TYPE,
	GENDER_KEY_TYPE,
	MARITAL_STATUS_KEY_TYPE,
	OPENING_KEY_TYPE,
	PREFERENCE_KEY_TYPE,
	REGISTER_STATUS_KEY_TYPE,
	RELATIONSHIP_KEY_TYPE,
	RELIGION_KEY_TYPE
} from "@/shared/model";
import { z } from "zod";

// ============COMMON======================

/**
 * @name 담당자
 */
export const MANAGER = z.object({
	managerId: z.nullable(z.string()), // 등록
	startDate: z.nullable(z.string()), // 등록
	name: z.nullable(z.string()), // 등록
	historyId: z.optional(z.string()),
	endDate: z.optional(z.nullable(z.string())),
	currentManager: z.optional(z.boolean())
});
export type IManager = z.infer<typeof MANAGER>;

export const REGISTER_MANAGER = z.object({
	managerId: z.nullable(z.string()), // 등록
	name: z.nullable(z.string()), // 등록
	startDate: z.nullable(z.string()), // 등록
	endDate: z.optional(z.nullable(z.string()))
});

export const REGISTER_APPROVAL_MANAGER = z.object({
	id: z.nullable(z.string()),
	name: z.nullable(z.string()),
	startDate: z.nullable(z.string()),
	endDate: z.optional(z.nullable(z.string()))
});

/**
 * @name 가족 개업 세무사 > 담당자
 */
const FAMILY_MANAGER = z.object({
	id: z.nullable(z.string()),
	name: z.nullable(z.string())
});
export type IFAMILY_MANAGER = z.infer<typeof FAMILY_MANAGER>;

/**
 * @name 소개자
 */
const INTRODUCER = z.object({
	id: z.string().nullable(),
	name: z.string().nullable()
});

// =========================================

/**
 * @name 기본정보
 */
export const BASIC_INFO = z.object({
	name: z.string().nullable(),
	areaBlock: z.boolean().nullable(),
	nameBeforeModified: z.string().nullable(),
	companyStatus: COMPANY_STATUS_KEY_TYPE,
	status: OPENING_KEY_TYPE,
	registerStatus: z.optional(REGISTER_STATUS_KEY_TYPE).nullable(),
	managers: z.preprocess<IManager[], z.ZodArray<typeof MANAGER>, IManager[]>(
		(arg) => (arg === null || arg === undefined ? [] : arg),
		z.array(MANAGER)
	)
});
export type BASIC_INFO_TYPE = z.infer<typeof BASIC_INFO>;

export const REGISTER_BASIC_INFO = z.object({
	name: z.string().nullable(),
	nameBeforeModified: z.string().nullable(),
	status: OPENING_KEY_TYPE,
	manager: REGISTER_MANAGER
});

export const REGISTER_APPROVAL_BASIC_INFO = z.object({
	name: z.string().nullable(),
	nameBeforeModified: z.string().nullable(),
	companyStatus: COMPANY_STATUS_KEY_TYPE,
	status: OPENING_KEY_TYPE,
	registerStatus: z.optional(REGISTER_STATUS_KEY_TYPE),
	manager: REGISTER_APPROVAL_MANAGER.nullable(),
	areaBlock: z.boolean().nullable(),
});

/**
 * @name 사무실 > 근무중 가족
 */
const CORPORATION_FAMILY_INFO = z.object({
	id: z.string().nullable(),
	name: z.string().nullable(), // 성명
	position: z.string().nullable(), // 직위
	relationship: z.string().nullable(), // 관계
	birthday: z.string().nullable(), // 출생년도(YYYY.MM.dd)
	etc: z.string().nullable() // 비고
});
export type CORPORATION_FAMILY_INFO_TYPE = z.infer<typeof CORPORATION_FAMILY_INFO>;

/**
 * @name 사무실
 */
export const REGISTER_CORPORATION_INFO = z.object({
	id: z.string().nullable(),
	type: CORPORATION_OPERATION_KEY_TYPE.nullable(),
	name: z.string().nullable(),
	detail: z.string().nullable(), // v2
	telephone: z.string().nullable(),
	fax: z.string().nullable(),
	jibunAddress: z.string().nullable(), // v2
	roadNameAddress: z.optional(z.nullable(z.string())), // v2
	employeeCount: z.number().nullable(),
	familyWorked: z.boolean().nullable(),
	coordinate: z.optional(
		z.object({
			longitude: z.number(), // 경도 x좌표
			latitude: z.number() // 위도 y좌표
		})
	), // v2
	familyInformations: z.array(CORPORATION_FAMILY_INFO) // v2
});
export type CORPORATION_INFO_TYPE = z.infer<typeof REGISTER_CORPORATION_INFO>;

export const MODIFY_CORPORATION_INFO = z.object({
	id: z.string().nullable(),
	type: CORPORATION_OPERATION_KEY_TYPE.nullable(),
	name: z.string().nullable(),
	detail: z.string().nullable(), // v2
	telephone: z.string().nullable(),
	fax: z.string().nullable(),
	jibunAddress: z.string().nullable(), // v2
	roadNameAddress: z.optional(z.nullable(z.string())), // v2
	employeeCount: z.number().nullable(),
	familyWorked: z.boolean().nullable(),
	longitude: z.number().nullable(), // 경도 x좌표
	latitude: z.number().nullable(), // 위도 y좌표
	familyInformations: z.array(CORPORATION_FAMILY_INFO) // v2
});

/**
 * @name 보험경력
 */
export const INSURANCE_EXPERIENCE = z.object({
	life: z.boolean().nullable(),
	indemnity: z.boolean().nullable(),
	ga: z.string().nullable()
});

/**
 * @name 접근경로
 */
export const ACCESS_ROUTE = z.object({
	inPersonManager: INTRODUCER,
	referer: z.string().nullable()
});

/**
 * @name G-Star 클럽
 */
export const G_STAR_CLUB = z.string();

/**
 * @name 가족 관계 > 가족정보
 */
const FAMILY_MEMBER = z.object({
	id: z.string().nullable(),
	name: z.string().nullable(),
	job: z.string().nullable(),
	gender: GENDER_KEY_TYPE,
	birthYear: z.string().nullable(),
	etc: z.string().nullable(),
	relationship: RELATIONSHIP_KEY_TYPE
});

/**
 * @name 가족 관계
 */
export const FAMILY_RELATIONSHIPS = z.object({
	maritalStatus: MARITAL_STATUS_KEY_TYPE,
	familyMemberInformations: z.array(FAMILY_MEMBER)
});

// =========================================

/**
 * @name 골프 핸디캡
 */
export const GOLF_HANDICAP = z.object({
	id: z.string().nullable(),
	date: z.string().nullable(),
	golfHandicap: z.string().nullable()
});
export type IGolfHandicap = z.infer<typeof GOLF_HANDICAP>;

/**
 * @name 개인정보
 */
export const PERSONAL_INFO = z.object({
	genderCode: z.number().nullable(),
	gender: GENDER_KEY_TYPE.nullable(),
	birthday: z.string().nullable(),
	realBirthday: z.string().nullable(),
	birthdayType: CALENDAR_KEY_TYPE.nullable(),
	birthplace: z.string().nullable(),
	phone: z.string().nullable(),
	email: z.string().nullable(),
	religion: RELIGION_KEY_TYPE.nullable(),
	bloodType: BLOOD_KEY_TYPE.nullable(),
	twelveZodiacAnimal: CHINESE_ZODIAC_SYMBOLS_KEY_TYPE.nullable(),
	golfPreference: PREFERENCE_KEY_TYPE.nullable(),
	golfHandicaps: z.preprocess<IGolfHandicap[], z.ZodArray<typeof GOLF_HANDICAP>, IGolfHandicap[]>(
		(val) => val ?? [],
		z.array(GOLF_HANDICAP)
	),
	hobby: z.optional(z.nullable(z.string())),
	hobbies: z.preprocess<string[], z.ZodArray<z.ZodString>, string[]>((val) => val ?? [], z.array(z.string()))
});

/**
 * @name 추가정보
 */
export const ADDITIONAL_INFO = z.object({
	numberOfTaxBookCustomer: z.number(),
	numberOfCorporationCustomer: z.number(),
	ratioOfCorporation: z.number().optional(),
	closeTaxAccountant: z.string().nullable()
});

/**
 * @name FC정보 > 세무사지점장(TABM) 등록
 */
const FINANCIAL_CONSULTANT_REGISTRATION_HISTORY = z.object({
	id: z.string().nullable(),
	fcCertificate: z.array(FC_CERTIFICATES_KEY_TYPE),
	certificateReason: z.string().nullable(),
	registeredBy: z.string().nullable(),
	companyId: z.string().nullable(),
	registeredDate: z.string().nullable(),
	resignedDate: z.string().nullable()
});

/**
 * @name FC정보
 */
export const FINANCIAL_CONSULTANT = z.array(FINANCIAL_CONSULTANT_REGISTRATION_HISTORY);

/**
 * @name 기타정보 > 가족 중 세무사
 */
const FAMILY_TAX_ACCOUNTANT = z.object({
	id: z.string().nullable(),
	relationship: z.string().nullable(),
	name: z.string().nullable(),
	address: z.string().nullable(),
	companyStatus: COMPANY_STATUS_KEY_TYPE.nullable(),
	manager: FAMILY_MANAGER.nullable()
});

/**
 * @name 기타정보 > 가족 중 보험설계사
 */
export const FAMILY_INSURANCE_PLANNER = z.object({
	id: z.string().nullable(),
	relationship: z.string().nullable(),
	companyName: z.string().nullable()
});

/**
 * @name 기타정보
 */
export const ETC_INFO = z.object({
	carBrand: z.string().nullable(),
	familyTaxAccountantInformations: z.array(FAMILY_TAX_ACCOUNTANT).nullable(),
	familyInsurancePlannerInformations: z.array(FAMILY_INSURANCE_PLANNER).nullable(),
	assetSize: z.string().nullable(),
	otherBusiness: z.string().nullable(),
	contract: z.string().nullable(),
	sns1: z.string().nullable(),
	sns2: z.string().nullable(),
	sns3: z.string().nullable()
});

// ===========================================

/**
 * @name 경력 리스트 > 경력
 */
const CAREER_HISTORY_ITEM = z.object({
	id: z.string().nullable(),
	workHistory: z.string().nullable(),
	isNationalTaxServiced: z.boolean()
});

/**
 * @name 경력 리스트
 */
export const CAREER_HISTORY_LIST = z.array(CAREER_HISTORY_ITEM);

/**
 * @name 학력 리스트 > 학력
 */
export const EDUCATION_ITEM = z.object({
	id: z.string().nullable(),
	education: EDUCATION_KEY_TYPE,
	educationDetail: z.string().nullable()
});

/**
 * @name 학력 리스트
 */
export const EDUCATION_INFO = z.array(EDUCATION_ITEM);

/**
 * @name 세무사 정보
 */
export const TAX_ACCOUNTANT_REGISTRATION_INFO = z.object({
	numberOfExam: z.string().nullable(),
	registrationNumber: z.string().nullable(),
	registeredDate: z.string().nullable(),
	closedDate: z.string().nullable(),
	deathDate: z.string().nullable()
});

// 세무사상담 상세 조회
/**
 * @name 추천고객
 */
export const RECOMMENDED_CUSTOMER_INFO = z.object({
	id: z.string().nullable(),
	customerType: z.string().nullable(),
	storeName: z.string().nullable(),
	representativeName: z.string().nullable(),
	telephone: z.string().nullable()
});
export type IRecommendedCustomerInfo = z.infer<typeof RECOMMENDED_CUSTOMER_INFO>;
